import { isMicrophonePermissionDeniedError } from "@jugl-web/utils/audio/requestMicrophoneAccess";
import { VoiceModeLogger } from "./VoiceModeLogger";
import {
  VoiceModePlayback,
  type VoiceModePlaybackStatus,
} from "./VoiceModePlayback";
import { VoiceModeSpeechRecognition } from "./VoiceModeSpeechRecognition";
import {
  VoiceModeTransport,
  type VoiceModeIncomingEvent,
  type VoiceModeTransportStatus,
} from "./VoiceModeTransport";
import { VoiceModeUserInputAnalysis } from "./VoiceModeUserInputAnalysis";
import type {
  VoiceModeFatalErrorReason,
  VoiceModeMicStatus,
  VoiceModeSessionOptions,
  VoiceModeSessionSnapshot,
} from "./types";

type SessionListener = () => void;

const QUOTA_EXCEEDED_ERROR_CODE = "quota_exceeded";

/**
 * Manages voice mode: handles transport, agent audio playback, speech recognition, and mic analysis.
 * UI can listen for updates and call methods on this class.
 */
export class VoiceModeSession {
  private readonly url: string;
  private readonly params: Record<string, string>;
  private readonly onEndCall: () => void;
  private readonly onError: (message: string) => void;
  private readonly onFatalError: (reason: VoiceModeFatalErrorReason) => void;

  private readonly listeners = new Set<SessionListener>();

  private transport: VoiceModeTransport | null = null;
  private playback: VoiceModePlayback | null = null;
  private recognition: VoiceModeSpeechRecognition | null = null;
  private userInputAnalysis: VoiceModeUserInputAnalysis | null = null;
  private logger: VoiceModeLogger = new VoiceModeLogger("VoiceModeSession");

  private transportStatus: VoiceModeTransportStatus = "idle";
  private playbackStatus: VoiceModePlaybackStatus = "idle";
  private micStatus: VoiceModeMicStatus = "idle";
  private isMuted = false;
  private isUserSpeaking = false;
  /**
   * Set to true after the user interrupts, until the backend confirms or a new turn starts.
   * While true, ignore old agent audio chunks.
   */
  private isDiscardingAgentStream = false;
  private waveformSamples: Float32Array<ArrayBuffer> | null = null;

  /**
   * A copy of the current state for useSyncExternalStore.
   * This is replaced with a new object only when state changes, so React only updates when needed.
   */
  private snapshot: VoiceModeSessionSnapshot = {
    transportStatus: "idle",
    playbackStatus: "idle",
    micStatus: "idle",
    isMuted: false,
    isUserSpeaking: false,
    isDiscardingAgentStream: false,
    waveformSamples: null,
  };

  /** True after recognition was disposed for agent audio playback (to avoid echo loops). */
  private isRecognitionSuspended = false;

  /** True after the backend signals `turn` (wire-side agent response complete). */
  private isAgentResponseCompleteOnWire = false;

  /** Queues incoming events to avoid race conditions. */
  private incomingEventChain: Promise<void> = Promise.resolve();

  private isStarted = false;
  private isDisposed = false;

  constructor(options: VoiceModeSessionOptions) {
    this.url = options.url;
    this.params = options.params;
    this.onEndCall = options.onEndCall ?? noop;
    this.onError = options.onError ?? noop;
    this.onFatalError = options.onFatalError ?? noop;
  }

  /**
   * Gets the latest snapshot. Reference stays the same until state updates.
   * Arrow function so it works with useSyncExternalStore.
   */
  public readonly getSnapshot = (): VoiceModeSessionSnapshot => {
    return this.snapshot;
  };

  /**
   * Adds a listener for changes. Returns a function to remove it.
   * Arrow function so it works with useSyncExternalStore.
   */
  public readonly subscribe = (listener: SessionListener): (() => void) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Boots mic analysis first; only then connects transport and ASR.
   * Safe to call again after stop() — e.g. after the user grants mic access in browser settings.
   */
  public start(): void {
    if (this.isDisposed) {
      throw new Error("VoiceModeSession has been disposed");
    }

    if (this.isStarted) {
      return;
    }

    this.logger.log("Starting session");
    this.isStarted = true;
    this.micStatus = "requesting";
    this.isAgentResponseCompleteOnWire = false;
    this.incomingEventChain = Promise.resolve();

    this.playback = new VoiceModePlayback({
      onStatusChange: (status) => {
        this.handlePlaybackStatusChange(status);
      },
    });

    this.transport = new VoiceModeTransport({
      url: this.url,
      params: this.params,
      onEventReceived: (event) => {
        this.enqueueIncomingEvent(event);
      },
      onStatusChange: (status) => {
        this.handleTransportStatusChange(status);
      },
    });

    const userInputAnalysis = new VoiceModeUserInputAnalysis({
      onSpeakingChange: (isSpeaking) => {
        this.handleUserSpeakingChange(isSpeaking);
      },
    });

    this.userInputAnalysis = userInputAnalysis;
    this.emit();

    userInputAnalysis
      .start()
      .then(() => {
        if (
          this.isDisposed ||
          !this.isStarted ||
          this.userInputAnalysis !== userInputAnalysis
        ) {
          return;
        }

        this.logger.log("Microphone access granted");
        this.micStatus = "granted";
        this.waveformSamples = userInputAnalysis.getWaveformSamples();
        this.emit();

        this.transport?.connect();

        if (!this.isMuted) {
          this.startSpeechRecognition();
        }
      })
      .catch((error: unknown) => {
        if (
          this.isDisposed ||
          !this.isStarted ||
          this.userInputAnalysis !== userInputAnalysis
        ) {
          return;
        }

        this.logger.error("Failed to start user input analysis", error);

        if (isMicrophonePermissionDeniedError(error)) {
          this.micStatus = "denied";
          this.emit();
          this.onFatalError("microphone-permission-denied");
        } else {
          this.micStatus = "idle";
          this.emit();
          this.onFatalError("unknown");
        }

        this.stop();
      });
  }

  /**
   * Cleans up resources but keeps the session ready to restart if needed.
   */
  public stop(): void {
    if (!this.isStarted) {
      return;
    }

    this.logger.log("Stopping session");
    this.isStarted = false;
    this.incomingEventChain = Promise.resolve();

    if (this.recognition) {
      this.recognition.dispose();
      this.recognition = null;
    }

    this.isRecognitionSuspended = false;
    this.isAgentResponseCompleteOnWire = false;
    this.isDiscardingAgentStream = false;
    this.isUserSpeaking = false;

    if (this.userInputAnalysis) {
      this.userInputAnalysis.dispose();
      this.userInputAnalysis = null;
    }

    this.waveformSamples = null;
    this.micStatus = "idle";

    if (this.transport) {
      this.transport.disconnect();
      this.transport = null;
    }

    this.transportStatus = "idle";

    if (this.playback) {
      this.playback.close();
      this.playback = null;
    }

    this.playbackStatus = "idle";
    this.emit();
  }

  /**
   * Permanently tears down the session. The instance cannot be restarted.
   */
  public dispose(): void {
    if (this.isDisposed) {
      return;
    }

    this.logger.log("Disposing session");
    this.stop();
    this.isDisposed = true;
    this.listeners.clear();
  }

  /**
   * Turns mute on or off. Mute pauses speech recognition, but mic analysis stays active.
   */
  public setMuted(muted: boolean): void {
    if (this.isDisposed || this.isMuted === muted) {
      return;
    }

    this.logger.log(muted ? "Mute on" : "Mute off");
    this.isMuted = muted;

    if (muted) {
      this.stopSpeechRecognitionForMute();
    } else if (!this.isRecognitionSuspended && !this.recognition) {
      this.startSpeechRecognition();
    } else {
      this.maybeResumeRecognitionAfterAgentAudio();
    }

    this.emit();
  }

  /**
   * Flips mute state.
   */
  public toggleMute(): void {
    this.setMuted(!this.isMuted);
  }

  /**
   * Sends a text message over the voice channel.
   */
  public sendText(text: string): void {
    if (this.isDisposed || !this.transport) {
      return;
    }

    const normalizedText = text.trim();

    if (normalizedText.length === 0) {
      return;
    }

    this.logger.log(`Sending text: "${normalizedText}"`);
    this.transport.sendText(normalizedText);
  }

  /**
   * Queues socket events so async audio appends cannot interleave with `turn`.
   */
  private enqueueIncomingEvent(event: VoiceModeIncomingEvent): void {
    this.incomingEventChain = this.incomingEventChain
      .then(async () => {
        if (this.isDisposed || !this.isStarted) {
          return;
        }

        await this.handleIncomingEvent(event);
      })
      .catch((error: unknown) => {
        this.logger.error("Failed to handle incoming voice event", error);
      });
  }

  /**
   * Reacts to socket events and manages the full voice interaction workflow.
   */
  private async handleIncomingEvent(
    event: VoiceModeIncomingEvent
  ): Promise<void> {
    if (this.isDisposed) {
      return;
    }

    if (event.type === "text") {
      // Text streaming is not relevant to the voice-mode session.
      return;
    }

    if (event.type === "audio") {
      this.logger.log("Transport: received audio");

      if (this.isDiscardingAgentStream) {
        return;
      }

      // A new agent audio stream means the previous wire turn (if any) is obsolete.
      this.isAgentResponseCompleteOnWire = false;

      // Kill recognition on the first audio chunk of a turn to avoid echo loops.
      if (!this.isRecognitionSuspended) {
        this.suspendSpeechRecognition();
      }

      await this.playback?.appendChunk(event.data);
      return;
    }

    if (event.type === "error") {
      this.logger.error("Transport: received error", event.data);

      if (event.data === QUOTA_EXCEEDED_ERROR_CODE) {
        this.handleFatalIncomingError(event.data);
      } else {
        this.onError(event.data);
      }
      return;
    }

    if (event.type === "turn") {
      this.logger.log("Transport: received turn end", event.data);

      // End the discard window when the backend confirms the agent response.
      this.clearDiscardAgentStream();

      // Wire turn is complete, but the playback may still be playing buffered audio.
      // Resume ASR only once local playback is idle.
      this.isAgentResponseCompleteOnWire = true;
      this.maybeResumeRecognitionAfterAgentAudio();

      return;
    }

    if (event.type === "interrupt") {
      this.logger.log("Transport: received interrupt");

      // End the discard window when the backend confirms the interruption.
      this.clearDiscardAgentStream();

      // Restart playback after interruption so it is ready for the next agent response.
      this.playback?.restart();

      return;
    }

    if (event.type === "end_call") {
      this.logger.log("Transport: received end_call");
      // The agent decided to end the call.
      this.onEndCall();
    }
  }

  /**
   * Surfaces an unrecoverable incoming error and tears the session down immediately.
   */
  private handleFatalIncomingError(errorData: string): void {
    if (this.isDisposed || !this.isStarted) {
      return;
    }

    this.onFatalError(
      errorData === QUOTA_EXCEEDED_ERROR_CODE ? "quota-exceeded" : "unknown"
    );

    this.stop();
  }

  /**
   * Tracks transport status and surfaces a fatal error when the channel cannot be joined.
   */
  private handleTransportStatusChange(status: VoiceModeTransportStatus): void {
    this.logger.log(`Transport status → ${status}`);
    this.transportStatus = status;
    this.emit();

    if (status === "failed") {
      this.logger.error("Voice channel join failed");
      this.onFatalError("channel-join-failed");
      this.stop();
    }
  }

  /**
   * Tracks playback status and resumes ASR when buffered agent audio has finished.
   */
  private handlePlaybackStatusChange(status: VoiceModePlaybackStatus): void {
    this.logger.log(`Playback status → ${status}`);
    this.playbackStatus = status;
    this.emit();

    if (status === "idle") {
      this.maybeResumeRecognitionAfterAgentAudio();
    }
  }

  /**
   * Opens the mic only after both: backend `turn`, and local agent playback idle.
   * Keeps recognition suspended while muted, but clears the suspension flag so unmute works.
   */
  private maybeResumeRecognitionAfterAgentAudio(): void {
    if (
      this.isDisposed ||
      !this.isStarted ||
      !this.isRecognitionSuspended ||
      !this.isAgentResponseCompleteOnWire ||
      this.isDiscardingAgentStream
    ) {
      return;
    }

    if (this.playback?.isPlaying()) {
      this.logger.log(
        "Wire turn complete; waiting for local playback to finish before ASR"
      );
      return;
    }

    this.isAgentResponseCompleteOnWire = false;

    if (this.isMuted) {
      this.logger.log(
        "Agent playback finished; recognition stays off while muted"
      );
      this.isRecognitionSuspended = false;
      return;
    }

    this.logger.log("Agent playback finished; restarting speech recognition");
    this.startSpeechRecognition();
  }

  /**
   * Forwards final ASR transcripts to the backend unless muted.
   */
  private handleUserSpeechFinalTranscript(transcript: string): void {
    if (this.isDisposed || this.isMuted || !this.transport) {
      return;
    }

    this.logger.log(`User final transcript: "${transcript}"`);
    this.transport.sendText(transcript);
  }

  /**
   * Creates and starts a new speech recognition instance.
   * No-ops while muted so turn-end / barge-in cannot reopen listening.
   * Surfaces a typed fatal error when ASR is unavailable.
   */
  private startSpeechRecognition(): void {
    if (this.isDisposed || this.isMuted) {
      return;
    }

    let recognition: VoiceModeSpeechRecognition;

    try {
      recognition = new VoiceModeSpeechRecognition({
        onUserSpeechFinalTranscript: (transcript) => {
          this.handleUserSpeechFinalTranscript(transcript);
        },
        onFatalError: (reason) => {
          this.handleSpeechRecognitionFatalError(reason);
        },
      });
    } catch {
      this.logger.error("Speech recognition unsupported");
      this.onFatalError("speech-recognition-unsupported");
      return;
    }

    this.logger.log("Starting speech recognition");
    this.recognition = recognition;
    this.isRecognitionSuspended = false;
    recognition.start();
  }

  /**
   * Updates mic status for permission failures, then forwards the fatal reason.
   */
  private handleSpeechRecognitionFatalError(
    reason: VoiceModeFatalErrorReason
  ): void {
    this.logger.error(`Speech recognition fatal error: ${reason}`);

    if (reason === "microphone-permission-denied") {
      this.micStatus = "denied";
      this.emit();
    }

    this.onFatalError(reason);
  }

  /**
   * Disposes recognition because agent audio started (echo protection).
   */
  private suspendSpeechRecognition(): void {
    if (this.isRecognitionSuspended) {
      return;
    }

    this.logger.log("Suspending speech recognition (agent audio)");

    const { recognition } = this;

    if (!recognition) {
      this.isRecognitionSuspended = true;
      return;
    }

    this.recognition = null;
    this.isRecognitionSuspended = true;
    recognition.dispose();
  }

  /**
   * Turns off speech recognition when the user mutes, but doesn't mark it as paused by agent audio.
   */
  private stopSpeechRecognitionForMute(): void {
    const { recognition } = this;

    if (!recognition) {
      return;
    }

    this.logger.log("Stopping speech recognition (mute)");
    this.recognition = null;
    recognition.dispose();
  }

  /**
   * Local barge-in: stop agent audio, ignore remaining audio,
   * and restart speech recognition so the next message can interrupt the agent.
   */
  private handleLocalBargeIn(): void {
    if (
      this.isMuted ||
      !this.isRecognitionSuspended ||
      this.isDiscardingAgentStream
    ) {
      return;
    }

    this.logger.log(
      "Local barge-in: stopping playback and restarting recognition"
    );

    this.isDiscardingAgentStream = true;
    this.playback?.stop();
    this.startSpeechRecognition();
    this.emit();
  }

  /**
   * Resets discard mode and lets playback continue.
   */
  private clearDiscardAgentStream(): void {
    if (!this.isDiscardingAgentStream) {
      return;
    }

    this.logger.log("Cleared discard-agent-stream window");
    this.isDiscardingAgentStream = false;
    this.playback?.start();
    this.emit();
  }

  /**
   * Voice detection callback: only allow barge-in if recognition is paused for agent audio.
   * Analysis keeps running when muted; barge-in is blocked in that case.
   */
  private handleUserSpeakingChange(isSpeaking: boolean): void {
    if (this.isDisposed) {
      return;
    }

    if (this.isUserSpeaking !== isSpeaking) {
      this.logger.log(
        isSpeaking ? "User speaking started" : "User speaking ended"
      );
    }

    this.isUserSpeaking = isSpeaking;
    this.emit();

    if (isSpeaking) {
      this.handleLocalBargeIn();
    }
  }

  /**
   * Updates the snapshot and notifies subscribers.
   */
  private emit(): void {
    this.snapshot = {
      transportStatus: this.transportStatus,
      playbackStatus: this.playbackStatus,
      micStatus: this.micStatus,
      isMuted: this.isMuted,
      isUserSpeaking: this.isUserSpeaking,
      isDiscardingAgentStream: this.isDiscardingAgentStream,
      waveformSamples: this.waveformSamples,
    };

    this.listeners.forEach((listener) => listener());
  }
}

const noop = () => {};
